import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from '../shell/icons'
import { translate, useFormat } from '../i18n'

/** En-tête d'écran : ce qu'on regarde, en une phrase, et les actions principales. */
export function PageHeader({ title, description, actions }: { title: string; description?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  )
}

/** Bloc titré qui regroupe une information cohérente. */
export function Block({
  title,
  subtitle,
  action,
  flush,
  className,
  children,
  as: Tag = 'section',
}: {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  action?: React.ReactNode
  flush?: boolean
  className?: string
  children: React.ReactNode
  as?: 'section' | 'div'
}) {
  return (
    <Tag className={`block${flush ? ' block-flush' : ''}${className ? ` ${className}` : ''}`}>
      {(title || action) && (
        <div className="block-head">
          <div className="block-head-text">
            {title && <span className="block-title">{title}</span>}
            {subtitle && <span className="block-sub">{subtitle}</span>}
          </div>
          {action}
        </div>
      )}
      {children}
    </Tag>
  )
}

/** Bloc repliable : pour le détail technique, fermé par défaut. */
export function CollapsibleBlock({
  title,
  subtitle,
  defaultOpen = false,
  children,
}: {
  title: React.ReactNode
  subtitle?: React.ReactNode
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  const id = useId()
  return (
    <section className="block">
      <button type="button" className="collapsible-head" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
        <Icon name="chevron" />
        <span className="block-title">{title}</span>
        {subtitle && <span className="block-sub">{subtitle}</span>}
      </button>
      {open && (
        <div id={id} style={{ marginTop: 14 }}>
          {children}
        </div>
      )}
    </section>
  )
}

export function Kpi({
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  label: string
  value: React.ReactNode
  sub?: React.ReactNode
  tone?: 'warn' | 'crit'
  onClick?: () => void
}) {
  const className = `block kpi${tone ? ` kpi-${tone}` : ''}`
  const content = (
    <>
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{value}</span>
      {sub && <span className="kpi-sub">{sub}</span>}
    </>
  )
  return onClick ? (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  )
}

export function ViewTabs<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T
  onChange: (v: T) => void
  options: Array<{ value: T; label: string; count?: number }>
  label: string
}) {
  return (
    <div className="view-tabs" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} className="view-tab" onClick={() => onChange(o.value)}>
          {o.label}
          {o.count !== undefined && <span className="nav-count">{o.count}</span>}
        </button>
      ))}
    </div>
  )
}

/**
 * Panneau latéral de détail. Remplace les fenêtres modales : la liste reste
 * visible derrière, et Échap ou un clic à côté referme le panneau.
 */
export function SidePanel({
  title,
  subtitle,
  onClose,
  footer,
  wide,
  children,
}: {
  title: React.ReactNode
  subtitle?: React.ReactNode
  onClose: () => void
  footer?: React.ReactNode
  wide?: boolean
  children: React.ReactNode
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  // Référence stable : un parent qui passe une fonction fléchée ne doit pas
  // relancer l'effet, sinon le focus quitterait le champ en cours de saisie.
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    panelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [])

  return (
    <>
      <div className="side-panel-scrim" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        className={`side-panel${wide ? ' side-panel-wide' : ''}`}
        role="dialog"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="side-panel-head">
          <div style={{ minWidth: 0 }}>
            <h2 id={titleId}>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button type="button" className="shell-icon-btn" aria-label={translate('common.close')} onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>
        <div className="side-panel-body">{children}</div>
        {footer && <div className="side-panel-foot">{footer}</div>}
      </div>
    </>
  )
}

export function Field({ label, children, note }: { label: string; children: React.ReactNode; note?: React.ReactNode }) {
  return (
    <div className="field">
      <dt>{label}</dt>
      <dd>{children}</dd>
      {note && <span className="field-note">{note}</span>}
    </div>
  )
}

export function StatusPill({ tone, children }: { tone?: 'ok' | 'warn' | 'crit' | 'info'; children: React.ReactNode }) {
  return <span className={`status-pill${tone && tone !== 'info' ? ` status-pill-${tone}` : ''}`}>{children}</span>
}

/** Barres horizontales : un libellé, une proportion, un nombre. */
export function BarList({ rows, onSelect }: { rows: Array<{ key: string; label: string; count: number; color?: string }>; onSelect?: (key: string) => void }) {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <ul className="bar-list">
      {rows.map((r) => {
        const inner = (
          <>
            <span className="bar-label" title={r.label}>{r.label}</span>
            <span className="bar-track" aria-hidden="true">
              <i style={{ width: `${(r.count / max) * 100}%`, background: r.color }} />
            </span>
            <span className="bar-count">{r.count}</span>
          </>
        )
        return (
          <li key={r.key}>
            {onSelect ? (
              <button type="button" className="bar-row" onClick={() => onSelect(r.key)}>
                {inner}
              </button>
            ) : (
              <div className="bar-row">{inner}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Pourcentage et ancienneté, dans la langue courante.
 *
 * Ce sont des hooks et non des fonctions libres : le séparateur décimal et le
 * signe de pourcentage changent de langue, et un composant qui les appelle doit
 * se réafficher quand la langue change.
 */
export function usePercent(): (part: number, total: number, digits?: number) => string {
  const fmt = useFormat()
  return (part, total, digits = 1) => fmt.percent(part, total, digits)
}

export function useRelativeDays(): (iso: string) => string {
  const fmt = useFormat()
  return (iso) => fmt.sinceDays(iso)
}
